import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { OwnerFormSteps, OwnerFormActions } from '../components/OwnerFormSteps';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import useOwnerDraft, { useAdminDraftPrefill, formatDraftTime } from '../hooks/useOwnerDraft';
import './SpaForm.css';

// Steps shown to owners on /list-your-property, in order
const OWNER_TAB_IDS = ['basic', 'location', 'services', 'media', 'contact', 'facilities', 'social', 'details', 'additional'];

// A usable coordinate: a real number inside the valid range. Zero is treated as
// missing, since number inputs fall back to 0 when cleared.
const hasCoordinate = (value, limit) => {
  if (value === '' || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 && Math.abs(n) <= limit;
};

const SpaForm = ({ mode = 'admin', ownerToken = '', ownerPhone = '', ownerEditId = '', onSuccess, onCancel, onCancelPath }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isOwnerMode = mode === 'owner';
  // Owners edit from the owner portal, which passes the listing id in;
  // admins edit from the /edit/:id route
  const editId = isOwnerMode ? ownerEditId : id;
  const isEdit = Boolean(editId);
  const [activeTab, setActiveTab] = useState('basic');

  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    city: '',
    state: '',
    country: 'India',
    zipCode: '',
    address: '',
    displayAddress: '',
    description: '',
    shortDescription: '',
    
    // Location
    latitude: '',
    longitude: '',
    cityLatitude: '',
    cityLongitude: '',
    
    // Contact
    whatsappNumber: '',
    phone: ownerPhone || '',
    email: '',
    website: '',
    alternatePhone: '',
    googleListingLink: '',
    
    // Spa Details
    rating: null,
    operatingHours: {
      openingTime: '09:00',
      closingTime: '21:00',
      daysOpen: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    
    // Policies
    cancellationPolicy: '',
    bookingPolicy: '',
    ageRestriction: '',
    
    // Facilities (array of facility IDs)
    facilities: [],
    
    // Status
    status: isOwnerMode ? 'pending' : 'active',
    featured: false,
    verified: false,
    showOnHome: false,
    currency: 'INR',
    
    // Social Links
    socialLinks: {
      facebook: '',
      instagram: '',
      youtube: ''
    }
  });

  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [availableSpaAmenities, setAvailableSpaAmenities] = useState([]);
  const [spaAmenitiesLoading, setSpaAmenitiesLoading] = useState(true);
  const [verificationSubscriptions, setVerificationSubscriptions] = useState([]);
  const [featuredSubscriptions, setFeaturedSubscriptions] = useState([]);
  const [verificationSubscriptionId, setVerificationSubscriptionId] = useState('');
  const [featuredSubscriptionId, setFeaturedSubscriptionId] = useState('');

  const [amenities, setAmenities] = useState([]);
  const [amenityInput, setAmenityInput] = useState('');
  const [services, setServices] = useState([]);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [serviceInput, setServiceInput] = useState({
    serviceName: '',
    price: '',
    cutPrice: '',
    duration: '',
    description: '',
    serviceAmenities: [],
    additionalAmenities: []
  });
  const [serviceAdditionalAmenityInput, setServiceAdditionalAmenityInput] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');
  const [nearbyAttractions, setNearbyAttractions] = useState([]);
  const [attractionInput, setAttractionInput] = useState({ name: '', distance: '', description: '' });
  const [languages, setLanguages] = useState([]);
  const [languageInput, setLanguageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  // The address must be chosen from the suggestion list: that is what gives
  // the listing its map position. `addressLookupDown` lifts the rule when the
  // suggestion service itself is unavailable, so nobody gets locked out.
  const [addressPicked, setAddressPicked] = useState(false);
  const [addressLookupDown, setAddressLookupDown] = useState(false);
  const [addressTouched, setAddressTouched] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityInput, setCityInput] = useState('');
  // Same rule as the address: the city must be chosen from the suggestion list
  const [cityPicked, setCityPicked] = useState(false);
  const [cityLookupDown, setCityLookupDown] = useState(false);
  const [cityTouched, setCityTouched] = useState(false);
  const [keepDisplayAddressSame, setKeepDisplayAddressSame] = useState(true);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const headerTitle = isEdit ? 'Edit Spa' : isOwnerMode ? 'Spa Registration' : 'Add New Spa';

  // ---- Draft support: owners resume where they left off; admins can prefill ----
  const initialFormDataRef = useRef(null);
  if (initialFormDataRef.current === null) initialFormDataRef.current = formData;

  const applyDraft = (saved, { asOwner = false } = {}) => {
    if (!saved || typeof saved !== 'object') return;
    if (saved.formData && typeof saved.formData === 'object') {
      setFormData(prev => ({
        ...prev,
        ...saved.formData,
        ...(asOwner ? { phone: ownerPhone, status: 'pending', featured: false, verified: false } : {})
      }));
    }
    if (Array.isArray(saved.amenities)) setAmenities(saved.amenities);
    if (Array.isArray(saved.services)) setServices(saved.services);
    if (Array.isArray(saved.nearbyAttractions)) setNearbyAttractions(saved.nearbyAttractions);
    if (Array.isArray(saved.languages)) setLanguages(saved.languages);
    if (typeof saved.addressInput === 'string') setAddressInput(saved.addressInput);
    // Drafts saved before this rule existed count as picked when they carry coordinates
    setAddressPicked(
      typeof saved.addressPicked === 'boolean'
        ? saved.addressPicked
        : Boolean(saved.formData?.address && saved.formData?.latitude)
    );
    if (typeof saved.cityInput === 'string') setCityInput(saved.cityInput);
    setCityPicked(
      typeof saved.cityPicked === 'boolean'
        ? saved.cityPicked
        : Boolean(saved.formData?.city && saved.formData?.cityLatitude)
    );
    if (typeof saved.keepDisplayAddressSame === 'boolean') setKeepDisplayAddressSame(saved.keepDisplayAddressSame);
    if (asOwner && typeof saved.activeTab === 'string' && OWNER_TAB_IDS.includes(saved.activeTab)) {
      setActiveTab(saved.activeTab);
    }
  };

  const draftEnabled = isOwnerMode && Boolean(ownerPhone) && !isEdit;
  const draft = useOwnerDraft({
    enabled: draftEnabled,
    type: 'spa',
    phone: ownerPhone,
    token: ownerToken,
    data: {
      activeTab,
      formData,
      amenities,
      services,
      nearbyAttractions,
      languages,
      addressInput,
      addressPicked,
      cityInput,
      cityPicked,
      keepDisplayAddressSame
    },
    onRestore: (saved) => applyDraft(saved, { asOwner: true })
  });

  const handleDiscardDraft = () => {
    draft.clear();
    setFormData({ ...initialFormDataRef.current, phone: ownerPhone || initialFormDataRef.current.phone });
    setAmenities([]);
    setServices([]);
    setNearbyAttractions([]);
    setLanguages([]);
    setAddressInput('');
    setAddressPicked(false);
    setAddressTouched(false);
    setCityInput('');
    setCityPicked(false);
    setCityTouched(false);
    setKeepDisplayAddressSame(true);
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setImages([]);
    setImagePreviews([]);
    setFeaturedImage('');
    setActiveTab('basic');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [searchParams] = useSearchParams();
  const prefillDraftId = !isOwnerMode && !isEdit ? searchParams.get('draft') : null;
  const prefill = useAdminDraftPrefill({
    draftId: prefillDraftId,
    onApply: (data) => applyDraft(data)
  });

  useEffect(() => {
    if (isOwnerMode && ownerPhone) {
      setFormData(prev => ({
        ...prev,
        phone: ownerPhone,
        status: 'pending',
        featured: false,
        verified: false
      }));
    }
  }, [isOwnerMode, ownerPhone]);

  useEffect(() => {
    const loadData = async () => {
      await fetchFacilities();
      await fetchSpaAmenities();
      if (!isOwnerMode) {
        await fetchSubscriptions();
      }
      if (isEdit) {
        await fetchSpa();
      }
    };
    loadData();
  }, [editId, isOwnerMode]);

  // Set subscription IDs after subscriptions are loaded (for edit mode)
  useEffect(() => {
    if (!isOwnerMode && isEdit && id && verificationSubscriptions.length > 0 && featuredSubscriptions.length > 0) {
      // Fetch spa again to get subscription IDs once subscriptions are loaded
      axios.get(`${API_BASE_URL}/admin/spas/${id}`)
        .then(response => {
          const spa = response.data.data;
          if (spa.verificationSubscription) {
            const subId = typeof spa.verificationSubscription === 'object' 
              ? spa.verificationSubscription._id 
              : spa.verificationSubscription;
            setVerificationSubscriptionId(subId);
          }
          if (spa.featuredSubscription) {
            const subId = typeof spa.featuredSubscription === 'object' 
              ? spa.featuredSubscription._id 
              : spa.featuredSubscription;
            setFeaturedSubscriptionId(subId);
          }
        })
        .catch(error => console.error('Error fetching spa subscriptions:', error));
    }
  }, [verificationSubscriptions.length, featuredSubscriptions.length, isOwnerMode, isEdit, id]);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/facilities/public`);
      setAvailableFacilities(response.data.data || []);
      setFacilitiesLoading(false);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setFacilitiesLoading(false);
    }
  };

  const fetchSpaAmenities = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/amenities/public`, { params: { type: 'SPA' } });
      setAvailableSpaAmenities(response.data.data || []);
      setSpaAmenitiesLoading(false);
    } catch (error) {
      console.error('Error fetching spa amenities:', error);
      setSpaAmenitiesLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const [verificationRes, featuredRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/verification-subscriptions?type=SPA`),
        axios.get(`${API_BASE_URL}/admin/featured-subscriptions?type=SPA`)
      ]);
      setVerificationSubscriptions(verificationRes.data.data || []);
      setFeaturedSubscriptions(featuredRes.data.data || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const fetchSpa = async () => {
    try {
      const response = await axios.get(
        isOwnerMode
          ? `${API_BASE_URL}/public/owners/me/listings/spas/${editId}`
          : `${API_BASE_URL}/admin/spas/${editId}`,
        isOwnerMode ? { headers: { Authorization: `Bearer ${ownerToken}` } } : undefined
      );
      const spa = response.data.data;
      
      // Extract facility IDs from populated facilities
      const facilityIds = spa.facilities 
        ? spa.facilities.map(f => typeof f === 'object' ? f._id : f).filter(Boolean)
        : [];
      
      setFormData({
        name: spa.name || '',
        city: spa.city || '',
        state: spa.state || '',
        country: spa.country || 'India',
        zipCode: spa.zipCode || '',
        address: spa.address || '',
        displayAddress: spa.displayAddress || spa.address || '',
        description: spa.description || '',
        shortDescription: spa.shortDescription || '',
        latitude: spa.latitude || '',
        longitude: spa.longitude || '',
        cityLatitude: spa.cityLatitude || '',
        cityLongitude: spa.cityLongitude || '',
        whatsappNumber: spa.whatsappNumber || '',
        phone: spa.phone || '',
        email: spa.email || '',
        website: spa.website || '',
        alternatePhone: spa.alternatePhone || '',
        googleListingLink: spa.googleListingLink || '',
        rating: spa.rating !== undefined && spa.rating !== null ? spa.rating : null,
        operatingHours: spa.operatingHours || {
          openingTime: '09:00',
          closingTime: '21:00',
          daysOpen: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        },
        cancellationPolicy: spa.cancellationPolicy || '',
        bookingPolicy: spa.bookingPolicy || '',
        ageRestriction: spa.ageRestriction || '',
        facilities: facilityIds,
        status: spa.status || 'active',
        featured: spa.featured || false,
        verified: spa.verified || false,
        showOnHome: spa.showOnHome || false,
        currency: spa.currency || 'INR',
        socialLinks: spa.socialLinks || {
          facebook: '',
          instagram: '',
          youtube: ''
        }
      });
      
      setAmenities(spa.amenities || []);
      const toAmenityIds = (arr) => (Array.isArray(arr) ? arr : [])
        .map(a => (a && typeof a === 'object' && a._id) ? a._id : a)
        .filter(id => id && typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id));
      const normalizedServices = (spa.services || []).map(svc => ({
        ...svc,
        serviceAmenities: toAmenityIds(svc.serviceAmenities),
        additionalAmenities: Array.isArray(svc.additionalAmenities) ? svc.additionalAmenities : []
      }));
      setServices(normalizedServices);
      setExistingImages(spa.images || []);
      setFeaturedImage(spa.featuredImage || '');
      setNearbyAttractions(spa.nearbyAttractions || []);
      setLanguages(spa.languages || []);
      setAddressInput(spa.address || '');
      setAddressPicked(Boolean(spa.address));
      setCityInput(spa.city || '');
      setCityPicked(Boolean(spa.city));
      setKeepDisplayAddressSame(spa.displayAddress === spa.address || !spa.displayAddress);
    } catch (error) {
      setError('Failed to fetch spa details');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('facility_')) {
      const facilityId = name.replace('facility_', '');
      setFormData({
        ...formData,
        facilities: checked
          ? [...formData.facilities, facilityId]
          : formData.facilities.filter(id => id !== facilityId)
      });
    } else if (name.startsWith('day_')) {
      const day = name.replace('day_', '');
      const currentDays = formData.operatingHours.daysOpen || [];
      const newDays = checked
        ? [...currentDays, day]
        : currentDays.filter(d => d !== day);
      setFormData({
        ...formData,
        operatingHours: {
          ...formData.operatingHours,
          daysOpen: newDays
        }
      });
    } else if (name === 'openingTime' || name === 'closingTime') {
      setFormData({
        ...formData,
        operatingHours: {
          ...formData.operatingHours,
          [name]: value
        }
      });
    } else if (name === 'rating') {
      // Handle rating - empty string means null (unknown)
      setFormData({
        ...formData,
        [name]: value === '' ? null : parseInt(value)
      });
    } else if (name.startsWith('socialLinks.')) {
      // Handle social links
      const socialField = name.replace('socialLinks.', '');
      setFormData({
        ...formData,
        socialLinks: {
          ...formData.socialLinks,
          [socialField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
      });
    }
  };

  const handleAddressInputChange = async (e) => {
    const value = e.target.value;
    setAddressInput(value);
    setAddressPicked(false);
    setAddressTouched(true);
    setFormData({
      ...formData,
      address: value,
      displayAddress: keepDisplayAddressSame ? value : formData.displayAddress,
      // Typed text no longer matches the place picked earlier
      latitude: '',
      longitude: ''
    });

    if (value.length > 2) {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
          params: { input: value }
        });
        setAddressSuggestions(response.data.data || []);
        setShowSuggestions(true);
        setAddressLookupDown(false);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        // Suggestions unavailable: allow a typed address rather than block the form
        setAddressLookupDown(true);
        if (error.response?.status !== 400) {
          setAddressSuggestions([]);
        }
      }
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePlaceSelect = async (place) => {
    setAddressPicked(true);
    setAddressInput(place.description);
    const newAddress = place.description;
    setFormData({
      ...formData,
      address: newAddress,
      displayAddress: keepDisplayAddressSame ? newAddress : formData.displayAddress
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id }
      });
      
      const placeDetails = response.data.data;
      setFormData({
        ...formData,
        address: newAddress,
        displayAddress: keepDisplayAddressSame ? newAddress : formData.displayAddress,
        latitude: placeDetails.latitude || formData.latitude,
        longitude: placeDetails.longitude || formData.longitude
      });
    } catch (error) {
      console.error('Error fetching place details:', error);
    }
  };

  const handleCityInputChange = async (e) => {
    const value = e.target.value;
    setCityInput(value);
    setCityPicked(false);
    setCityTouched(true);
    setFormData({
      ...formData,
      city: value,
      // Typed text no longer matches the city picked earlier
      cityLatitude: '',
      cityLongitude: ''
    });

    if (value.length > 2) {
      try {
        // Cities only: an area, temple or landmark must never be saved as the city
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
          params: { input: value, types: 'cities' }
        });
        setCitySuggestions(response.data.data || []);
        setShowCitySuggestions(true);
        setCityLookupDown(false);
      } catch (error) {
        console.error('Error fetching city autocomplete:', error);
        // Suggestions unavailable: allow a typed city rather than block the form
        setCityLookupDown(true);
        if (error.response?.status !== 400) {
          setCitySuggestions([]);
        }
      }
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const handleCitySelect = async (place) => {
    setCityPicked(true);
    setCityInput(place.description);
    setFormData({
      ...formData,
      city: place.description
    });
    setShowCitySuggestions(false);
    setCitySuggestions([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id }
      });
      
      const placeDetails = response.data.data;
      // Every listing stores its city the same way ("City, State, Country"),
      // so one place never ends up under several spellings.
      const cityName = placeDetails.canonicalCity || place.description;
      setCityInput(cityName);
      setFormData({
        ...formData,
        city: cityName,
        state: placeDetails.state || formData.state,
        country: placeDetails.country || formData.country,
        cityLatitude: placeDetails.latitude || formData.cityLatitude,
        cityLongitude: placeDetails.longitude || formData.cityLongitude
      });
    } catch (error) {
      console.error('Error fetching city place details:', error);
    }
  };

  const handleAddAmenity = () => {
    if (amenityInput.trim()) {
      setAmenities([...amenities, amenityInput.trim()]);
      setAmenityInput('');
    }
  };

  const handleRemoveAmenity = (index) => {
    setAmenities(amenities.filter((_, i) => i !== index));
  };

  const handleServiceAmenityToggle = (amenityId) => {
    const current = serviceInput.serviceAmenities || [];
    const next = current.includes(amenityId)
      ? current.filter(id => id !== amenityId)
      : [...current, amenityId];
    setServiceInput({ ...serviceInput, serviceAmenities: next });
  };

  const handleAddServiceAdditionalAmenity = () => {
    if (serviceAdditionalAmenityInput.trim()) {
      setServiceInput({
        ...serviceInput,
        additionalAmenities: [...(serviceInput.additionalAmenities || []), serviceAdditionalAmenityInput.trim()]
      });
      setServiceAdditionalAmenityInput('');
    }
  };

  const handleRemoveServiceAdditionalAmenity = (index) => {
    setServiceInput({
      ...serviceInput,
      additionalAmenities: (serviceInput.additionalAmenities || []).filter((_, i) => i !== index)
    });
  };

  const handleEditService = (index) => {
    // If already editing a different service, cancel that first
    if (editingServiceIndex !== null && editingServiceIndex !== index) {
      handleCancelEditService();
    }
    
    const service = services[index];
    setServiceInput({
      serviceName: service.serviceName || '',
      price: service.price !== undefined && service.price !== null ? String(service.price) : '',
      cutPrice: service.cutPrice !== undefined && service.cutPrice !== null ? String(service.cutPrice) : '',
      duration: service.duration || '',
      description: service.description || '',
      serviceAmenities: Array.isArray(service.serviceAmenities) ? [...service.serviceAmenities] : [],
      additionalAmenities: Array.isArray(service.additionalAmenities) ? [...service.additionalAmenities] : []
    });
    setEditingServiceIndex(index);
    // Scroll to form
    document.querySelector('.service-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelEditService = () => {
    setEditingServiceIndex(null);
    setServiceInput({
      serviceName: '',
      price: '',
      cutPrice: '',
      duration: '',
      description: '',
      serviceAmenities: [],
      additionalAmenities: []
    });
    setServiceAdditionalAmenityInput('');
  };

  const handleAddService = () => {
    if (serviceInput.serviceName && serviceInput.price && serviceInput.duration) {
      const newService = {
        serviceName: serviceInput.serviceName,
        price: parseFloat(serviceInput.price),
        cutPrice: serviceInput.cutPrice ? parseFloat(serviceInput.cutPrice) : null,
        duration: serviceInput.duration,
        description: serviceInput.description || '',
        serviceAmenities: Array.isArray(serviceInput.serviceAmenities) ? serviceInput.serviceAmenities : [],
        additionalAmenities: Array.isArray(serviceInput.additionalAmenities) ? serviceInput.additionalAmenities : []
      };

      if (editingServiceIndex !== null) {
        // Update existing service
        const updatedServices = [...services];
        updatedServices[editingServiceIndex] = newService;
        setServices(updatedServices);
        setEditingServiceIndex(null);
      } else {
        // Add new service
        setServices([...services, newService]);
      }

      // Reset form
      setServiceInput({
        serviceName: '',
        price: '',
        cutPrice: '',
        duration: '',
        description: '',
        serviceAmenities: [],
        additionalAmenities: []
      });
      setServiceAdditionalAmenityInput('');
    }
  };

  const handleRemoveService = (index) => {
    if (editingServiceIndex === index) {
      handleCancelEditService();
    }
    setServices(services.filter((_, i) => i !== index));
  };

  const handleAddAttraction = () => {
    if (attractionInput.name.trim()) {
      setNearbyAttractions([...nearbyAttractions, { ...attractionInput }]);
      setAttractionInput({ name: '', distance: '', description: '' });
    }
  };

  const handleRemoveAttraction = (index) => {
    setNearbyAttractions(nearbyAttractions.filter((_, i) => i !== index));
  };

  const handleAddLanguage = () => {
    if (languageInput.trim()) {
      setLanguages([...languages, languageInput.trim()]);
      setLanguageInput('');
    }
  };

  const handleRemoveLanguage = (index) => {
    setLanguages(languages.filter((_, i) => i !== index));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    
    // Create preview URLs for selected images
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const handleRemoveNewImage = (index) => {
    // Revoke the object URL to free memory
    if (imagePreviews[index]) {
      URL.revokeObjectURL(imagePreviews[index]);
    }
    
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSetFeaturedFromNew = (index) => {
    // Mark which new image should be featured
    // We'll use the index to identify it after upload
    setFeaturedImage(`new_image_${index}`);
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const handleSetFeaturedImage = (imagePath) => {
    setFeaturedImage(imagePath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Step tabs can be clicked directly, so the Location rules are checked
      // again here rather than relying on the Next button alone
      const locationErrors = getLocationErrors();
      if (locationErrors.length > 0) {
        setError(`Location Details: ${locationErrors.join('. ')}.`);
        setActiveTab('location');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (isOwnerMode && !ownerToken) {
        setError('Please verify your phone number to continue.');
        setLoading(false);
        return;
      }
      const submitData = new FormData();
      if (prefillDraftId) submitData.append('ownerDraftId', prefillDraftId);
      
      // Basic Information
      submitData.append('name', formData.name);
      submitData.append('city', formData.city);
      submitData.append('state', formData.state);
      submitData.append('country', formData.country);
      submitData.append('zipCode', formData.zipCode);
      submitData.append('address', formData.address);
      submitData.append('displayAddress', formData.displayAddress || formData.address);
      submitData.append('description', formData.description);
      submitData.append('shortDescription', formData.shortDescription);
      
      // Location
      submitData.append('latitude', formData.latitude);
      submitData.append('longitude', formData.longitude);
      submitData.append('cityLatitude', formData.cityLatitude);
      submitData.append('cityLongitude', formData.cityLongitude);
      
      // Contact
      submitData.append('whatsappNumber', formData.whatsappNumber);
      submitData.append('phone', isOwnerMode ? ownerPhone : formData.phone);
      submitData.append('email', formData.email);
      submitData.append('website', formData.website);
      submitData.append('alternatePhone', formData.alternatePhone || '');
      submitData.append('googleListingLink', formData.googleListingLink || '');
      
      // Spa Details
      submitData.append('rating', formData.rating === null ? '' : formData.rating);
      submitData.append('operatingHours', JSON.stringify(formData.operatingHours));
      
      // Policies
      submitData.append('cancellationPolicy', formData.cancellationPolicy);
      submitData.append('bookingPolicy', formData.bookingPolicy);
      submitData.append('ageRestriction', formData.ageRestriction);
      
      // Facilities & Amenities
      submitData.append('amenities', JSON.stringify(amenities));
      submitData.append('facilities', JSON.stringify(formData.facilities));
      
      // Services
      submitData.append('services', JSON.stringify(services));
      
      // Additional
      submitData.append('nearbyAttractions', JSON.stringify(nearbyAttractions));
      submitData.append('languages', JSON.stringify(languages));
      submitData.append('currency', formData.currency);
      const resolvedStatus = isOwnerMode ? 'pending' : formData.status;
      const resolvedFeatured = isOwnerMode ? false : formData.featured;
      const resolvedVerified = isOwnerMode ? false : formData.verified;
      submitData.append('status', resolvedStatus);
      submitData.append('featured', resolvedFeatured);
      submitData.append('verified', resolvedVerified);
      // Admin-only: owners can never put themselves on the home screen
      submitData.append('showOnHome', isOwnerMode ? false : Boolean(formData.showOnHome));
      submitData.append('socialLinks', JSON.stringify(formData.socialLinks));
      if (!isOwnerMode && resolvedFeatured && featuredSubscriptionId) {
        submitData.append('featuredSubscriptionId', featuredSubscriptionId);
      }
      if (!isOwnerMode && resolvedVerified && verificationSubscriptionId) {
        submitData.append('verificationSubscriptionId', verificationSubscriptionId);
      }
      // Determine featured image and reorder new images if needed
      let finalFeaturedImage = featuredImage;
      let imagesToUpload = [...images];
      let previewsToKeep = [...imagePreviews];
      
      // If a new image is selected as featured, move it to the front
      if (finalFeaturedImage && finalFeaturedImage.startsWith('new_image_')) {
        const featuredIndex = parseInt(finalFeaturedImage.replace('new_image_', ''));
        if (featuredIndex >= 0 && featuredIndex < imagesToUpload.length) {
          // Move featured image to first position
          const [featuredImage] = imagesToUpload.splice(featuredIndex, 1);
          const [featuredPreview] = previewsToKeep.splice(featuredIndex, 1);
          imagesToUpload.unshift(featuredImage);
          previewsToKeep.unshift(featuredPreview);
          // Backend will use first image as featured
          finalFeaturedImage = '';
        }
      }
      
      // If no featured image set, use first existing image
      if (!finalFeaturedImage && existingImages.length > 0) {
        finalFeaturedImage = existingImages[0];
      }
      
      submitData.append('featuredImage', finalFeaturedImage || '');
      submitData.append('existingImages', JSON.stringify(existingImages));

      imagesToUpload.forEach((image) => {
        submitData.append('images', image);
      });
      
      // Clean up preview URLs
      previewsToKeep.forEach(url => URL.revokeObjectURL(url));

      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(isOwnerMode && ownerToken ? { Authorization: `Bearer ${ownerToken}` } : {})
      };

      if (isOwnerMode) {
        let ownerResult = null;
        if (isEdit) {
          ownerResult = (await axios.put(`${API_BASE_URL}/public/owners/me/listings/spas/${editId}`, submitData, { headers })).data;
        } else {
          ownerResult = (await axios.post(`${API_BASE_URL}/public/owners/spas`, submitData, { headers })).data;
        }
        draft.clear();
        if (onSuccess) {
          onSuccess(ownerResult);
        } else {
          navigate('/');
        }
      } else if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/spas/${editId}`, submitData, { headers });
        navigate('/sy-admin/spas');
      } else {
        await axios.post(`${API_BASE_URL}/admin/spas`, submitData, { headers });
        navigate('/sy-admin/spas');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save spa');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isOwnerMode) {
      if (onCancel) {
        onCancel();
        return;
      }
      if (onCancelPath) {
        navigate(onCancelPath);
        return;
      }
      navigate('/');
      return;
    }
    navigate('/sy-admin/spas');
  };

  // Owners see the must-have steps first and the optional extras last.
  // Admins keep the original layout.
  const tabs = isOwnerMode
    ? [
        { id: 'basic', label: 'Basic Info' },
        { id: 'location', label: 'Location' },
        { id: 'services', label: 'Services' },
        { id: 'media', label: 'Photos' },
        { id: 'contact', label: 'Contacts' },
        { id: 'facilities', label: 'Facilities' },
        { id: 'social', label: 'Social Links (Optional)' },
        { id: 'details', label: 'Details & Policies (Optional)' },
        { id: 'additional', label: 'Additional Details (Optional)' }
      ]
    : [
        { id: 'basic', label: 'Basic Info' },
        { id: 'location', label: 'Location' },
        { id: 'contact', label: 'Contact' },
        { id: 'details', label: 'Details & Policies' },
        { id: 'facilities', label: 'Facilities' },
        { id: 'services', label: 'Services' },
        { id: 'media', label: 'Media' },
        { id: 'social', label: 'Social Links' },
        { id: 'additional', label: 'Additional' }
      ];

  const isLastTab = activeTab === tabs[tabs.length - 1].id;
  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const nextTab = currentTabIndex < tabs.length - 1 ? tabs[currentTabIndex + 1] : null;

  // Location rules, shared by the Location step and the final submit
  const getLocationErrors = () => {
    const errors = [];
    if (!formData.address || !formData.address.trim()) {
      errors.push('Address is required');
    } else if (!addressPicked && !addressLookupDown) {
      errors.push('Please choose the address from the suggestion list. A typed address is not accepted');
    }
    if (!hasCoordinate(formData.latitude, 90) || !hasCoordinate(formData.longitude, 180)) {
      errors.push('Spa Latitude and Spa Longitude are required. Picking the address from the list fills them in');
    }
    if (!formData.city || !formData.city.trim()) {
      errors.push('City is required');
    } else if (!cityPicked && !cityLookupDown) {
      errors.push('Please choose the city from the suggestion list. A typed city is not accepted');
    }
    if (!formData.state || !formData.state.trim()) {
      errors.push('State is required');
    }
    if (!hasCoordinate(formData.cityLatitude, 90) || !hasCoordinate(formData.cityLongitude, 180)) {
      errors.push('City Latitude and City Longitude are required. Picking the city from the list fills them in');
    }
    return errors;
  };

  // Validate current tab before moving to next
  const validateCurrentTab = (tabId) => {
    const errors = [];

    switch (tabId) {
      case 'basic':
        if (!formData.name || !formData.name.trim()) {
          errors.push('Spa Name is required');
        }
        if (isOwnerMode && (!formData.whatsappNumber || !formData.whatsappNumber.trim())) {
          errors.push('WhatsApp Number is required');
        }
        break;
      case 'location':
        errors.push(...getLocationErrors());
        break;
      case 'contact':
        if (!isOwnerMode && (!formData.phone || !formData.phone.trim())) {
          errors.push('Phone number is required');
        }
        break;
      case 'services':
        if (!services || services.length === 0) {
          errors.push('At least one Service is required');
        } else {
          const invalidServices = services.filter(s => !s.serviceName || !s.serviceName.trim() || !s.price || !s.duration || !s.duration.trim());
          if (invalidServices.length > 0) {
            errors.push('All services must have Service Name, Final Price After Discount, and Duration');
          }
        }
        break;
      case 'media':
        const totalImages = (images && images.length) + (existingImages && existingImages.length);
        if (totalImages === 0) {
          errors.push('At least one Image is required');
        }
        break;
      default:
        // Other tabs (details, facilities, social, additional) don't have required fields
        break;
    }

    return errors;
  };

  const handleNext = (e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Don't proceed if already on last tab
    if (isLastTab) {
      return false;
    }

    // Validate current tab before moving to next
    const validationErrors = validateCurrentTab(activeTab);
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    // Clear any previous errors
    setError('');

    // Persist the step the vendor just completed
    if (draftEnabled) {
      draft.save();
    }

    if (nextTab) {
      setActiveTab(nextTab.id);
      // Scroll to top of form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    return false;
  };

  // Field groups that sit in different tabs depending on who is filling the
  // form: owners get contact essentials up front, admins keep them under Contact.
  const renderWhatsAppAndPhoneFields = () => (
    <>
      <div className="form-group">
        <label>WhatsApp Number *</label>
        <input
          type="text"
          name="whatsappNumber"
          value={formData.whatsappNumber}
          onChange={handleChange}
          required
          placeholder="e.g., +919876543210"
        />
        {isOwnerMode && (
          <small className="form-hint">Customers will message this number to book.</small>
        )}
      </div>
      <div className="form-group">
        <label>Phone Number *</label>
        <input
          type="text"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required={isOwnerMode}
          readOnly={isOwnerMode}
          placeholder="e.g., +919876543210"
        />
        {isOwnerMode && (
          <small className="form-hint">Your login number. Guests do not see it.</small>
        )}
      </div>
    </>
  );

  const renderListingLinksField = () => (
    <div className="form-group">
      <label>{isOwnerMode ? 'Listing link (optional)' : 'Google Listing Link'}</label>
      <input
        type={isOwnerMode ? 'text' : 'url'}
        name="googleListingLink"
        value={formData.googleListingLink}
        onChange={handleChange}
        placeholder={isOwnerMode ? 'e.g. https://g.page/..., https://www.justdial.com/...' : 'Optional Google Maps / Business profile link'}
      />
      {isOwnerMode && (
        <small className="form-hint">Your page on Google or Justdial, if you have one.</small>
      )}
    </div>
  );

  const renderProfileFields = () => (
    <>
      <div className="form-row">
        <div className="form-group">
          <label>Rating</label>
          <select name="rating" value={formData.rating || ''} onChange={handleChange}>
            <option value="">Unknown</option>
            <option value={1}>1 Star</option>
            <option value={2}>2 Stars</option>
            <option value={3}>3 Stars</option>
            <option value={4}>4 Stars</option>
            <option value={5}>5 Stars</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Spa Short Description (optional)</label>
        <input
          type="text"
          name="shortDescription"
          value={formData.shortDescription}
          onChange={handleChange}
          placeholder="Brief description (for listings)"
          maxLength={200}
        />
      </div>

      <div className="form-group">
        <label>Spa Long Description (optional)</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="6"
          placeholder="Detailed description of the spa"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Currency</label>
          <select name="currency" value={formData.currency} onChange={handleChange}>
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="GBP">GBP (£)</option>
          </select>
        </div>
      </div>
    </>
  );

  const content = (
    <div className="container">
      {!isOwnerMode && (
        <div className="spa-form-header">
          <h1>{headerTitle}</h1>
          {isOwnerMode && !isEdit && (
            <p className="form-hint" style={{ marginTop: '8px' }}>
              Complete your listing details to get your spa verified and published.
              Your progress is saved after every step, so you can come back later.
            </p>
          )}
        </div>
      )}

        {isOwnerMode && draft.restoredAt && (
          <div className="owner-draft-notice">
            <span>
              We restored the details you saved on {formatDraftTime(draft.restoredAt)}.
              Photos are not kept between visits, so please select them again before submitting.
            </span>
            <button type="button" className="btn btn-secondary" onClick={handleDiscardDraft}>
              Start fresh
            </button>
          </div>
        )}
        {prefill.draft && (
          <div className="owner-draft-notice">
            <span>
              Prefilled from the owner draft for <strong>{prefill.draft.phone}</strong>
              {' '}(last saved {formatDraftTime(prefill.draft.lastSavedAt)}). Photos are not part of drafts.
              Creating this listing marks the owner&rsquo;s registration as completed and removes the draft.
            </span>
          </div>
        )}
        {prefill.error && <div className="error-message" style={{ marginBottom: 16 }}>{prefill.error}</div>}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="spa-form">
          {/* Tab Navigation */}
          {isOwnerMode ? (
            <OwnerFormSteps tabs={tabs} activeTab={activeTab} onSelect={setActiveTab} />
          ) : (
            <div className="form-tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="form-section">
                <h2>Basic Information</h2>
                <div className="form-group">
                  <label>Spa Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                {isOwnerMode ? (
                  <>
                    {renderWhatsAppAndPhoneFields()}
                    {renderListingLinksField()}
                  </>
                ) : (
                  renderProfileFields()
                )}

                {!isOwnerMode && (
                  <div className="subscription-switches-container">
                    <div className="subscription-switch-group">
                      <label className="subscription-switch-label">
                        <div className="subscription-switch-content">
                          <div className="subscription-switch-header">
                            <span className="subscription-switch-icon">✓</span>
                            <span className="subscription-switch-title">Verified Spa</span>
                          </div>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              name="verified"
                              checked={formData.verified}
                              onChange={handleChange}
                              className="toggle-input"
                            />
                            <span className="toggle-slider"></span>
                          </div>
                        </div>
                      </label>
                      {formData.verified && (
                        <div className="subscription-select-wrapper">
                          <select
                            value={verificationSubscriptionId}
                            onChange={(e) => setVerificationSubscriptionId(e.target.value)}
                            className="subscription-select"
                            required
                          >
                            <option value="">Select Verification Subscription *</option>
                            {verificationSubscriptions
                              .filter(sub => sub.status === 'active')
                              .map(sub => (
                                <option key={sub._id} value={sub._id}>
                                  {sub.name} - {sub.duration} {sub.durationUnit} - {sub.currency === 'INR' ? '₹' : sub.currency} {sub.price}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="subscription-switch-group">
                      <label className="subscription-switch-label">
                        <div className="subscription-switch-content">
                          <div className="subscription-switch-header">
                            <span className="subscription-switch-icon">⭐</span>
                            <span className="subscription-switch-title">Featured Spa</span>
                          </div>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              name="featured"
                              checked={formData.featured}
                              onChange={handleChange}
                              className="toggle-input"
                            />
                            <span className="toggle-slider"></span>
                          </div>
                        </div>
                      </label>
                      {formData.featured && (
                        <div className="subscription-select-wrapper">
                          <select
                            value={featuredSubscriptionId}
                            onChange={(e) => setFeaturedSubscriptionId(e.target.value)}
                            className="subscription-select"
                            required
                          >
                            <option value="">Select Featured Subscription *</option>
                            {featuredSubscriptions
                              .filter(sub => sub.status === 'active')
                              .map(sub => (
                                <option key={sub._id} value={sub._id}>
                                  {sub.name} - {sub.duration} {sub.durationUnit} - {sub.currency === 'INR' ? '₹' : sub.currency} {sub.price}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <div className="subscription-switch-group">
                      <label className="subscription-switch-label">
                        <div className="subscription-switch-content">
                          <div className="subscription-switch-header">
                            <span className="subscription-switch-icon">🏠</span>
                            <span className="subscription-switch-title">Show on Home</span>
                          </div>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              name="showOnHome"
                              checked={Boolean(formData.showOnHome)}
                              onChange={handleChange}
                              className="toggle-input"
                            />
                            <span className="toggle-slider"></span>
                          </div>
                        </div>
                      </label>
                      <small className="form-hint" style={{ display: 'block', marginTop: 8 }}>
                        Lists this spa in the app's home screen without the Featured tag and without a subscription.
                      </small>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className="form-section">
                <h2>Location Details</h2>
                
                {/* Spa Address Section */}
                <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e2e8f0' }}>
                  <h3 style={{ marginBottom: '16px', color: '#0f172a', fontSize: '1.25rem' }}>Spa Address</h3>
                  
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={addressInput || formData.address}
                      onChange={handleAddressInputChange}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                      required
                      autoComplete="off"
                      className={addressTouched && !addressPicked && !addressLookupDown ? 'input-invalid' : ''}
                      placeholder="Start typing, then pick your spa address from the list"
                    />
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        {addressSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handlePlaceSelect(suggestion)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>
                              {suggestion.structured_formatting?.main_text || suggestion.description}
                            </div>
                            {suggestion.structured_formatting?.secondary_text && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {suggestion.structured_formatting.secondary_text}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {addressPicked ? (
                      <small className="form-hint address-picked">✓ Address selected from the list</small>
                    ) : addressLookupDown ? (
                      <small className="form-hint">Address suggestions are unavailable right now, so you can type the address.</small>
                    ) : addressTouched && (addressInput || formData.address) ? (
                      <small className="form-hint address-not-picked">
                        Pick your address from the list that appears. Typing alone is not accepted.
                      </small>
                    ) : (
                      <small className="form-hint">
                        Start typing, then choose your address from the list. This sets the spa location on the map.
                      </small>
                    )}
                  </div>

                  {!isOwnerMode && (
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={keepDisplayAddressSame}
                          onChange={(e) => {
                            setKeepDisplayAddressSame(e.target.checked);
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                displayAddress: formData.address
                              });
                            }
                          }}
                          style={{ marginRight: '8px' }}
                        />
                        Keep Display Address same as Address
                      </label>
                    </div>
                  )}

                  {!isOwnerMode && (
                    <div className="form-group">
                      <label>Display Address (optional)</label>
                      <input
                        type="text"
                        name="displayAddress"
                        value={formData.displayAddress}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            displayAddress: e.target.value
                          });
                          if (keepDisplayAddressSame) {
                            setKeepDisplayAddressSame(false);
                          }
                        }}
                        disabled={keepDisplayAddressSame}
                        placeholder="Enter display address (can be different from actual address)"
                      />
                      <small className="form-hint">Address to display to users (can be different from actual address)</small>
                    </div>
                  )}

                  {!isOwnerMode && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Spa Latitude *</label>
                        <input
                          type="number"
                          name="latitude"
                          required
                          value={formData.latitude}
                          onChange={handleChange}
                          step="any"
                          placeholder="e.g., 28.6139"
                        />
                        <small className="form-hint">Required. Filled in when you pick the address from the list</small>
                      </div>
                      <div className="form-group">
                        <label>Spa Longitude *</label>
                        <input
                          type="number"
                          name="longitude"
                          required
                          value={formData.longitude}
                          onChange={handleChange}
                          step="any"
                          placeholder="e.g., 77.2090"
                        />
                        <small className="form-hint">Required. Filled in when you pick the address from the list</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* City Information Section */}
                <div>
                  <h3 style={{ marginBottom: '16px', color: '#0f172a', fontSize: '1.25rem' }}>City Information</h3>
                  
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={cityInput || formData.city}
                      onChange={handleCityInputChange}
                      onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                      onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
                      required
                      autoComplete="off"
                      className={cityTouched && !cityPicked && !cityLookupDown ? 'input-invalid' : ''}
                      placeholder="Start typing, then pick the city from the list"
                    />
                    {cityPicked ? (
                      <small className="form-hint address-picked">✓ City selected from the list</small>
                    ) : cityLookupDown ? (
                      <small className="form-hint">City suggestions are unavailable right now, so you can type the city.</small>
                    ) : cityTouched && (cityInput || formData.city) ? (
                      <small className="form-hint address-not-picked">
                        Pick the city from the list that appears. Typing alone is not accepted.
                      </small>
                    ) : (
                      <small className="form-hint">
                        Start typing, then choose the city from the list. This fills State, Country and the city coordinates.
                      </small>
                    )}
                    {showCitySuggestions && citySuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        {citySuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handleCitySelect(suggestion)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>
                              {suggestion.structured_formatting?.main_text || suggestion.description}
                            </div>
                            {suggestion.structured_formatting?.secondary_text && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {suggestion.structured_formatting.secondary_text}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {isOwnerMode && formData.state && (
                      <small className="form-hint address-picked">✓ {formData.state}{formData.country ? `, ${formData.country}` : ''}</small>
                    )}
                  </div>

                  {!isOwnerMode && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  )}

                  {!isOwnerMode && (
                    <div className="form-group">
                      <label>Zip Code (optional)</label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                      />
                    </div>
                  )}

                  {!isOwnerMode && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>City Latitude *</label>
                        <input
                          type="number"
                          name="cityLatitude"
                          required
                          value={formData.cityLatitude}
                          onChange={handleChange}
                          step="any"
                          placeholder="e.g., 28.6139"
                        />
                        <small className="form-hint">Required. City center latitude, filled in when you pick the city from the list</small>
                      </div>
                      <div className="form-group">
                        <label>City Longitude *</label>
                        <input
                          type="number"
                          name="cityLongitude"
                          required
                          value={formData.cityLongitude}
                          onChange={handleChange}
                          step="any"
                          placeholder="e.g., 77.2090"
                        />
                        <small className="form-hint">Required. City center longitude, filled in when you pick the city from the list</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="form-section">
                <h2>{isOwnerMode ? 'Contacts' : 'Contact Information'}</h2>
                {isOwnerMode ? (
                  <p className="form-hint" style={{ marginBottom: '16px' }}>
                    Everything here is optional. WhatsApp and phone were already captured in Basic Info.
                  </p>
                ) : (
                  renderWhatsAppAndPhoneFields()
                )}
                <div className="form-group">
                  <label>Email (optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="spa@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Website (optional)</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Alternate Mobile Number (optional)</label>
                  <input
                    type="tel"
                    name="alternatePhone"
                    value={formData.alternatePhone}
                    onChange={handleChange}
                    placeholder="Optional backup contact number"
                  />
                </div>
                {!isOwnerMode && renderListingLinksField()}
              </div>
            )}

            {/* Details & Policies Tab */}
            {activeTab === 'details' && (
              <div className="form-section">
                <h2>Spa Details & Policies</h2>
                {isOwnerMode && renderProfileFields()}
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Opening Time</label>
                    <input
                      type="time"
                      name="openingTime"
                      value={formData.operatingHours.openingTime}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Closing Time</label>
                    <input
                      type="time"
                      name="closingTime"
                      value={formData.operatingHours.closingTime}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Days Open</label>
                  <div className="days-checkbox-grid">
                    {daysOfWeek.map(day => (
                      <label key={day} className="day-checkbox">
                        <input
                          type="checkbox"
                          name={`day_${day}`}
                          checked={formData.operatingHours.daysOpen.includes(day)}
                          onChange={handleChange}
                        />
                        <span>{day}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Cancellation Policy (optional)</label>
                  <textarea
                    name="cancellationPolicy"
                    value={formData.cancellationPolicy}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe cancellation policy"
                  />
                </div>

                <div className="form-group">
                  <label>Booking Policy (optional)</label>
                  <textarea
                    name="bookingPolicy"
                    value={formData.bookingPolicy}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe booking policy"
                  />
                </div>

                <div className="form-group">
                  <label>Age Restriction (optional)</label>
                  <input
                    type="text"
                    name="ageRestriction"
                    value={formData.ageRestriction}
                    onChange={handleChange}
                    placeholder="e.g., Minimum age 16 years"
                  />
                </div>
              </div>
            )}

            {/* Facilities Tab */}
            {activeTab === 'facilities' && (
              <div className="form-section">
                <h2>Facilities & Amenities</h2>
                
                {facilitiesLoading ? (
                  <div>Loading facilities...</div>
                ) : availableFacilities.length === 0 ? (
                  <div className="no-facilities">
                    <p>{isOwnerMode ? 'No facilities to choose from yet. You can skip this step.' : <>No facilities available. Please add facilities from the <Link to="/sy-admin/facilities">Facilities Management</Link> page.</>}</p>
                  </div>
                ) : (
                  <>
                    {/* Group facilities by category */}
                    {['general', 'room', 'dining', 'recreation', 'business', 'transport', 'other'].map(category => {
                      const categoryFacilities = availableFacilities.filter(f => f.category === category);
                      if (categoryFacilities.length === 0) return null;
                      
                      return (
                        <div key={category} className="facility-category">
                          <h3>{category.charAt(0).toUpperCase() + category.slice(1)} Facilities</h3>
                          <div className="facilities-grid">
                            {categoryFacilities.map(facility => (
                              <label key={facility._id} className="facility-checkbox">
                                <input
                                  type="checkbox"
                                  name={`facility_${facility._id}`}
                                  checked={formData.facilities.includes(facility._id)}
                                  onChange={handleChange}
                                />
                                <span>
                                  {facility.icon && <span style={{ marginRight: '6px' }}>{facility.icon}</span>}
                                  {facility.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="form-group" style={{ marginTop: '24px' }}>
                  <label>Additional Amenities</label>
                  <div className="amenity-input">
                    <input
                      type="text"
                      value={amenityInput}
                      onChange={(e) => setAmenityInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAmenity())}
                      placeholder="Add amenity (e.g., Aromatherapy, Steam Room)"
                    />
                    <button type="button" onClick={handleAddAmenity} className="btn btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="amenity-list">
                    {amenities.map((amenity, index) => (
                      <span key={index} className="badge badge-primary">
                        {amenity}
                        <button
                          type="button"
                          onClick={() => handleRemoveAmenity(index)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="form-section">
                <h2>Services & Treatments</h2>
                
                <div className="service-form">
                  <h3>{editingServiceIndex !== null ? 'Edit Service' : 'Add New Service'}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Service Name *</label>
                      <input
                        type="text"
                        placeholder="e.g., Swedish Massage, Facial Treatment"
                        value={serviceInput.serviceName}
                        onChange={(e) => setServiceInput({ ...serviceInput, serviceName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Final Price After Discount *</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={serviceInput.price}
                        onChange={(e) => setServiceInput({ ...serviceInput, price: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label>Service Price</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={serviceInput.cutPrice}
                        onChange={(e) => setServiceInput({ ...serviceInput, cutPrice: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                      <small className="form-hint">Price before discount, shown struck through (optional)</small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Duration *</label>
                      <input
                        type="text"
                        placeholder="e.g., 60 minutes, 90 minutes"
                        value={serviceInput.duration}
                        onChange={(e) => setServiceInput({ ...serviceInput, duration: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Service Description (optional)</label>
                    <textarea
                      placeholder="Describe the service"
                      value={serviceInput.description}
                      onChange={(e) => setServiceInput({ ...serviceInput, description: e.target.value })}
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Service Amenities (optional)</label>
                    {spaAmenitiesLoading ? (
                      <div>Loading amenities...</div>
                    ) : availableSpaAmenities.length === 0 ? (
                      <div className="no-facilities" style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', color: '#92400e' }}>
                        {isOwnerMode ? 'No amenities to choose from yet. Type your own below.' : <>No SPA amenities available. Add amenities (type: SPA) from <Link to="/sy-admin/amenities">Amenities Management</Link>.</>}
                      </div>
                    ) : (
                      <div className="facilities-grid">
                        {availableSpaAmenities.map(amenity => (
                          <label key={amenity._id} className="facility-checkbox">
                            <input
                              type="checkbox"
                              checked={(serviceInput.serviceAmenities || []).includes(amenity._id)}
                              onChange={() => handleServiceAmenityToggle(amenity._id)}
                            />
                            <span>
                              {amenity.icon && <span style={{ marginRight: '6px' }}>{amenity.icon}</span>}
                              {amenity.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Additional Amenities</label>
                    <div className="amenity-input">
                      <input
                        type="text"
                        value={serviceAdditionalAmenityInput}
                        onChange={(e) => setServiceAdditionalAmenityInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddServiceAdditionalAmenity())}
                        placeholder="Add amenity (e.g., Aromatherapy, Towels)"
                      />
                      <button type="button" onClick={handleAddServiceAdditionalAmenity} className="btn btn-secondary">
                        Add
                      </button>
                    </div>
                    <div className="amenity-list">
                      {(serviceInput.additionalAmenities || []).map((amenity, index) => (
                        <span key={index} className="badge badge-primary">
                          {amenity}
                          <button
                            type="button"
                            onClick={() => handleRemoveServiceAdditionalAmenity(index)}
                            className="remove-btn"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" onClick={handleAddService} className="btn btn-primary">
                      {editingServiceIndex !== null ? 'Update Service' : 'Add Service'}
                    </button>
                    {editingServiceIndex !== null && (
                      <button type="button" onClick={handleCancelEditService} className="btn btn-secondary">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                <div className="services-list" style={{ marginTop: '32px' }}>
                  <h3>Added Services</h3>
                  {services.map((service, index) => (
                    <div key={index} className="service-item">
                      <div>
                        <strong>{service.serviceName}</strong> - {formData.currency} {service.price}
                        {service.cutPrice && (
                          <span style={{ textDecoration: 'line-through', color: '#64748b', marginLeft: '8px' }}>
                            {formData.currency} {service.cutPrice}
                          </span>
                        )}
                        <br />
                        Duration: {service.duration}
                        {service.description && <p style={{ marginTop: '8px', color: '#64748b' }}>{service.description}</p>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleEditService(index)}
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveService(index)}
                          className="btn btn-danger"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Media Tab */}
            {activeTab === 'media' && (
              <div className="form-section">
                <h2>Photos *</h2>
                <p className="form-hint photos-intro">
                  Add at least one photo. The cover photo is what guests see first in search results.
                </p>

                <div className="image-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    id="spa-image-upload"
                    className="image-upload-input"
                  />
                  <label htmlFor="spa-image-upload" className="image-upload-label">
                    <div className="image-upload-content">
                      <span className="image-upload-icon">📷</span>
                      <span className="image-upload-text">
                        {existingImages.length + imagePreviews.length > 0 ? 'Add more photos' : 'Tap to choose photos'}
                      </span>
                      <span className="image-upload-hint">JPG, PNG or WebP · up to 5 MB each · pick several at once</span>
                    </div>
                  </label>
                </div>

                {existingImages.length + imagePreviews.length > 0 ? (
                  <div className="photos-block">
                    <div className="photos-block-head">
                      <h3>Photos ({existingImages.length + imagePreviews.length})</h3>
                      <span className="form-hint">Tap ⭐ to choose the cover photo.</span>
                    </div>
                    <div className="images-grid">
                      {existingImages.map((image, index) => (
                        <div key={`existing-${image}`} className={`image-preview-card ${featuredImage === image ? 'is-cover' : ''}`}>
                          <div className="image-preview-wrapper">
                            <img src={`${API_BASE_URL_WITHOUT_API}${image}`} alt={`Spa ${index + 1}`} />
                            {featuredImage === image && <span className="cover-badge">⭐ Cover</span>}
                          </div>
                          <div className="image-preview-actions">
                            <button
                              type="button"
                              onClick={() => handleSetFeaturedImage(image)}
                              className={`btn btn-sm ${featuredImage === image ? 'btn-primary' : 'btn-secondary'}`}
                              disabled={featuredImage === image}
                            >
                              {featuredImage === image ? 'Cover photo' : '⭐ Set as cover'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingImage(index)}
                              className="btn btn-sm btn-danger"
                              aria-label="Remove photo"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                      {imagePreviews.map((preview, index) => (
                        <div key={`new-${index}`} className={`image-preview-card ${featuredImage === `new_image_${index}` ? 'is-cover' : ''}`}>
                          <div className="image-preview-wrapper">
                            <img src={preview} alt={`New photo ${index + 1}`} />
                            {featuredImage === `new_image_${index}` && <span className="cover-badge">⭐ Cover</span>}
                            <span className="new-photo-badge">New</span>
                          </div>
                          <div className="image-preview-actions">
                            <button
                              type="button"
                              onClick={() => handleSetFeaturedFromNew(index)}
                              className={`btn btn-sm ${featuredImage === `new_image_${index}` ? 'btn-primary' : 'btn-secondary'}`}
                              disabled={featuredImage === `new_image_${index}`}
                            >
                              {featuredImage === `new_image_${index}` ? 'Cover photo' : '⭐ Set as cover'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewImage(index)}
                              className="btn btn-sm btn-danger"
                              aria-label="Remove photo"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="form-hint photos-empty">No photos yet. Listings with photos get far more enquiries.</p>
                )}
              </div>
            )}

            {/* Additional Tab */}
            {activeTab === 'social' && (
              <div className="form-section">
                <h2>Social Media Links</h2>
                <p className="form-hint" style={{ marginBottom: '24px' }}>
                  Add your social media profiles to help customers connect with you.
                </p>
                
                <div className="form-group">
                  <label>
                    <span className="social-icon">📘</span>
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    name="socialLinks.facebook"
                    value={formData.socialLinks.facebook}
                    onChange={handleChange}
                    placeholder="https://www.facebook.com/yourpage"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <span className="social-icon">📷</span>
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    name="socialLinks.instagram"
                    value={formData.socialLinks.instagram}
                    onChange={handleChange}
                    placeholder="https://www.instagram.com/yourprofile"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <span className="social-icon">📺</span>
                    YouTube URL
                  </label>
                  <input
                    type="url"
                    name="socialLinks.youtube"
                    value={formData.socialLinks.youtube}
                    onChange={handleChange}
                    placeholder="https://www.youtube.com/channel/yourchannel"
                  />
                </div>
              </div>
            )}

            {activeTab === 'additional' && (
              <div className="form-section">
                <h2>Additional Information</h2>
                
                <div className="form-group">
                  <label>Nearby Attractions (optional)</label>
                  <div className="attraction-input">
                    <input
                      type="text"
                      placeholder="Attraction Name"
                      value={attractionInput.name}
                      onChange={(e) => setAttractionInput({ ...attractionInput, name: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Distance"
                      value={attractionInput.distance}
                      onChange={(e) => setAttractionInput({ ...attractionInput, distance: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={attractionInput.description}
                      onChange={(e) => setAttractionInput({ ...attractionInput, description: e.target.value })}
                    />
                    <button type="button" onClick={handleAddAttraction} className="btn btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="attractions-list">
                    {nearbyAttractions.map((attraction, index) => (
                      <div key={index} className="attraction-item">
                        <strong>{attraction.name}</strong>
                        {attraction.distance && <span> - {attraction.distance}</span>}
                        {attraction.description && <p>{attraction.description}</p>}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttraction(index)}
                          className="btn btn-danger"
                          style={{ fontSize: '12px', padding: '4px 8px', marginTop: '8px' }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Languages Spoken (optional)</label>
                  <div className="amenity-input">
                    <input
                      type="text"
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
                      placeholder="e.g., English, Hindi"
                    />
                    <button type="button" onClick={handleAddLanguage} className="btn btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="amenity-list">
                    {languages.map((language, index) => (
                      <span key={index} className="badge badge-primary">
                        {language}
                        <button
                          type="button"
                          onClick={() => handleRemoveLanguage(index)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {isOwnerMode ? (
            <OwnerFormActions
              tabs={tabs}
              activeTab={activeTab}
              onSelect={setActiveTab}
              onNext={handleNext}
              isEdit={isEdit}
              loading={loading}
              onCancel={handleCancel}
              submitLabel="Submit spa"
              draft={draftEnabled ? draft : null}
            />
          ) : (
            <div className="form-actions">
              {isEdit ? (
                /* Editing an existing spa: allow saving from any tab, not just the last one */
                <>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Update Spa'}
                  </button>
                  {!isLastTab && (
                    <button 
                      type="button" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNext(e);
                      }} 
                      className="btn btn-secondary"
                    >
                      Next
                    </button>
                  )}
                </>
              ) : isLastTab ? (
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Create Spa'}
                </button>
              ) : (
                <button 
                  type="button" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNext(e);
                  }} 
                  className="btn btn-primary"
                >
                  Next
                </button>
              )}
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
              {isOwnerMode && draft.savedAt && (
                <span className="form-draft-status">
                  {draft.synced ? 'Progress saved' : 'Saved on this device only (server unreachable)'}{' '}
                  {formatDraftTime(draft.savedAt)}
                </span>
              )}
            </div>
          )}
        </form>
      </div>
  );

  return isOwnerMode ? content : <Layout>{content}</Layout>;
};

export default SpaForm;

